import type {
  Equipment,
  EquipmentBatchGetRequest,
  EquipmentBatchGetResponse,
  EquipmentPage,
  EquipmentPatchRequest,
  EquipmentWriteRequest,
} from '../api/active/generated/equipment';
import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';
import { createCatalogMediaApi } from '../catalog-media/catalogMediaApi';
import {
  type CatalogReadIndex,
  validateCatalogReadIndex,
} from '../quote-integration/catalogReadIndex';
import {
  notifyQuoteCatalogMutation,
  quoteCatalogSessionResource,
} from '../quote-integration/catalogSessionResource';

export class EquipmentApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

/** Includes response-body consumption, not just receipt of HTTP headers. */
export function withEquipmentDeadline<T>(
  request: (signal: AbortSignal) => Promise<T>,
  timeoutMs = 30_000,
  upstreamSignal?: AbortSignal | null,
): Promise<T> {
  const controller = new AbortController();
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (complete: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      upstreamSignal?.removeEventListener('abort', canceled);
      complete();
    };
    const canceled = () => {
      finish(() =>
        reject(
          new EquipmentApiError(
            null,
            'REQUEST_ABORTED',
            '设备请求已取消，请保留当前输入并核对保存结果。',
          ),
        ),
      );
      controller.abort(upstreamSignal?.reason);
    };
    const timer = window.setTimeout(() => {
      finish(() =>
        reject(
          new EquipmentApiError(
            null,
            'REQUEST_TIMEOUT',
            '设备请求超时，请保留当前输入并重试。',
          ),
        ),
      );
      controller.abort(new DOMException('Timeout', 'TimeoutError'));
    }, timeoutMs);
    upstreamSignal?.addEventListener('abort', canceled, { once: true });
    if (upstreamSignal?.aborted) {
      canceled();
      return;
    }
    void Promise.resolve()
      .then(() => request(controller.signal))
      .then(
        (value) => finish(() => resolve(value)),
        (cause) => finish(() => reject(cause)),
      );
  });
}

export type EquipmentListQuery = {
  page: number;
  pageSize: number;
  search?: string;
  category?: string;
  status?: 'active' | 'inactive';
  retailPriceTax13Min?: number;
  retailPriceTax13Max?: number;
  rentalPriceMin?: number;
  rentalPriceMax?: number;
  updatedFrom?: string;
  updatedTo?: string;
};

type Versioned<T> = { value: T; etag: string };

async function error(result: Response): Promise<EquipmentApiError> {
  let problem: {
    code?: string;
    errorCode?: string;
    detail?: string;
    title?: string;
  } = {};
  try {
    problem = (await result.json()) as typeof problem;
  } catch {
    // Preserve the HTTP failure when an upstream proxy returns no JSON.
  }
  const code = problem.code ?? problem.errorCode;
  const detail =
    result.status === 503 &&
    code === 'DEPENDENCY_UNAVAILABLE' &&
    problem.detail === 'The request could not be completed safely.'
      ? '暂无法完成设备操作，请稍后重试。'
      : (problem.detail ??
        problem.title ??
        `设备请求失败（HTTP ${result.status}）。`);
  return new EquipmentApiError(result.status, code, detail);
}

function queryString(query: EquipmentListQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  for (const [key, value] of Object.entries(query)) {
    if (
      key === 'page' ||
      key === 'pageSize' ||
      value === undefined ||
      value === ''
    )
      continue;
    params.set(key, String(value));
  }
  return params.toString();
}

async function response<T>(
  transport: ActiveTransport,
  path: string,
  init?: RequestInit,
) {
  return withEquipmentDeadline(
    async (signal) => {
      const result = await transport.requestResponse(path, { ...init, signal });
      if (!result.ok) throw await error(result);
      return (await result.json()) as T;
    },
    30_000,
    init?.signal,
  );
}

export function createEquipmentApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  const media = createCatalogMediaApi(transport, csrfToken);
  const mutationHeaders: Record<string, string> = csrfToken
    ? { 'X-CSRF-Token': csrfToken }
    : {};
  const write = async <T>(
    path: string,
    method: 'POST' | 'PATCH',
    body: unknown,
    options: {
      etag?: string;
      idempotencyKey?: string;
      signal?: AbortSignal;
    } = {},
  ): Promise<Versioned<T>> => {
    const mutationScope = quoteCatalogSessionResource.currentScope();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Idempotency-Key': options.idempotencyKey ?? randomUuid(),
      ...mutationHeaders,
    };
    if (options.etag) headers['If-Match'] = options.etag;
    return withEquipmentDeadline(
      async (signal) => {
        const result = await transport.requestResponse(path, {
          method,
          headers,
          body: JSON.stringify(body),
          signal,
        });
        if (!result.ok) throw await error(result);
        const value = {
          value: (await result.json()) as T,
          etag: result.headers.get('ETag') ?? '',
        };
        notifyQuoteCatalogMutation(
          mutationScope,
          'devices',
          typeof value.value === 'object' &&
            value.value !== null &&
            'id' in value.value
            ? [String(value.value.id)]
            : [],
          ['packages', 'plans'],
        );
        return value;
      },
      30_000,
      options.signal,
    );
  };
  return {
    media,
    catalogIndex: async (signal?: AbortSignal) =>
      validateCatalogReadIndex(
        await response<CatalogReadIndex>(
          transport,
          '/equipment/catalog-index?status=active',
          { signal, cache: 'no-store' },
        ),
      ),
    // Compatibility aliases keep the existing equipment adapter surface while
    // delegating every upload action to the single shared catalog primitive.
    createUploadTicket: media.createUploadTicket,
    putObject: media.putObject,
    completeUpload: media.completeUpload,
    getAsyncOperation: media.getAsyncOperation,
    list: (query: EquipmentListQuery, signal?: AbortSignal) =>
      response<EquipmentPage>(transport, `/equipment?${queryString(query)}`, {
        signal,
        cache: 'no-store',
      }),
    get: async (
      id: string,
      signal?: AbortSignal,
    ): Promise<Versioned<Equipment>> => {
      return withEquipmentDeadline(
        async (boundedSignal) => {
          const result = await transport.requestResponse(
            `/equipment/${encodeURIComponent(id)}`,
            { signal: boundedSignal, cache: 'no-store' },
          );
          if (!result.ok) throw await error(result);
          return {
            value: (await result.json()) as Equipment,
            etag: result.headers.get('ETag') ?? '',
          };
        },
        30_000,
        signal,
      );
    },
    // Read-only POST: no idempotency key, ETag or catalog invalidation.
    batchGet: (equipmentIds: readonly string[], signal?: AbortSignal) =>
      response<EquipmentBatchGetResponse>(transport, '/equipment:batch-get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...mutationHeaders },
        body: JSON.stringify({
          equipmentIds: [...equipmentIds],
        } satisfies EquipmentBatchGetRequest),
        signal,
        cache: 'no-store',
      }),
    create: (body: EquipmentWriteRequest, idempotencyKey?: string) =>
      write<Equipment>('/equipment', 'POST', body, { idempotencyKey }),
    update: (
      id: string,
      etag: string,
      body: EquipmentPatchRequest,
      idempotencyKey?: string,
    ) =>
      write<Equipment>(`/equipment/${encodeURIComponent(id)}`, 'PATCH', body, {
        etag,
        idempotencyKey,
      }),
    async remove(id: string, etag: string, idempotencyKey?: string) {
      const mutationScope = quoteCatalogSessionResource.currentScope();
      const result = await transport.requestResponse(
        `/equipment/${encodeURIComponent(id)}`,
        {
          method: 'DELETE',
          headers: {
            'If-Match': etag,
            'Idempotency-Key': idempotencyKey ?? randomUuid(),
            ...mutationHeaders,
          },
        },
      );
      if (!result.ok) throw await error(result);
      notifyQuoteCatalogMutation(
        mutationScope,
        'devices',
        [id],
        ['packages', 'plans'],
      );
    },
  };
}
